/**
 * Leveling Types
 * 
 * Types for the Leveling capability that helps ensure items are at
 * comparable altitudes before prioritization.
 * 
 * Altitude is a canonical property of an item that answers:
 * "What kind of thing is this, structurally?"
 * 
 * Altitude is dimension-derived, not label-based:
 * - Computed from time horizon, work size, and impact surface
 * - Results in a level from 1-5 (Tactical to Strategic)
 * 
 * The system protects decision quality by enforcing structural coherence,
 * not merely recording user intent.
 */

import type { AltitudeLevel } from './itemLifecycle';

// ============================================================================
// Altitude Lifecycle States
// ============================================================================

/**
 * Lifecycle states for altitude assignment
 * 
 * - proposed: User has declared an altitude, but it hasn't been reviewed
 * - under_review: Altitude is being validated/reviewed by facilitator
 * - accepted: Altitude has been confirmed as canonical
 */
export type AltitudeLifecycleState = 'proposed' | 'under_review' | 'accepted';

/**
 * Labels for altitude lifecycle states
 */
export const ALTITUDE_LIFECYCLE_LABELS: Record<AltitudeLifecycleState, string> = {
  proposed: 'Proposed',
  under_review: 'Under Review',
  accepted: 'Accepted',
};

/**
 * Colors for altitude lifecycle state badges
 */
export const ALTITUDE_LIFECYCLE_COLORS: Record<AltitudeLifecycleState, { bg: string; text: string }> = {
  proposed: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  under_review: { bg: 'bg-blue-100', text: 'text-blue-700' },
  accepted: { bg: 'bg-green-100', text: 'text-green-700' },
};

// ============================================================================
// Scoping Inputs for Assisted Altitude Selection
// ============================================================================

/**
 * Time horizon options
 * "If this went well, when would we see meaningful impact?"
 */
export type TimeHorizon = 'days' | 'weeks' | '1-3_months' | '3-12_months' | '1+_years';

/**
 * Work size options (effort)
 */
export type WorkSize = '1-2_weeks' | '3-8_weeks' | '9-26_weeks' | '27-52_weeks' | '52+_weeks';

/**
 * Primary impact surface options
 */
export type ImpactSurface = 'one_screen' | 'one_workflow' | 'multiple_workflows' | 'multiple_products' | 'company_wide';

/**
 * Scoping inputs for assisted altitude selection
 * 
 * These inputs are used to predict the appropriate altitude for an item
 * before the user explicitly selects one.
 */
export interface AltitudeScopingInputs {
  timeHorizon?: TimeHorizon;
  workSize?: WorkSize;
  impactSurface?: ImpactSurface;
}

/**
 * Labels and descriptions for time horizon options
 */
export const TIME_HORIZON_OPTIONS: { value: TimeHorizon; label: string; description: string }[] = [
  { value: 'days', label: 'Days', description: 'Impact visible within days' },
  { value: 'weeks', label: 'Weeks', description: 'Impact visible within weeks' },
  { value: '1-3_months', label: '1-3 Months', description: 'Impact visible in 1-3 months' },
  { value: '3-12_months', label: '3-12 Months', description: 'Impact visible in 3-12 months' },
  { value: '1+_years', label: '1+ Years', description: 'Long-term strategic impact' },
];

/**
 * Labels and descriptions for work size options
 */
export const WORK_SIZE_OPTIONS: { value: WorkSize; label: string; description: string }[] = [
  { value: '1-2_weeks', label: '1-2 Weeks', description: '1-2 team-weeks of effort' },
  { value: '3-8_weeks', label: '3-8 Weeks', description: '3-8 team-weeks of effort' },
  { value: '9-26_weeks', label: '9-26 Weeks', description: '2-6 months of effort' },
  { value: '27-52_weeks', label: '27-52 Weeks', description: '6-12 months of effort' },
  { value: '52+_weeks', label: '52+ Weeks', description: 'More than a year of effort' },
];

/**
 * Labels and descriptions for impact surface options
 */
export const IMPACT_SURFACE_OPTIONS: { value: ImpactSurface; label: string; description: string }[] = [
  { value: 'one_screen', label: 'One Screen', description: 'Affects a single screen or component' },
  { value: 'one_workflow', label: 'One Workflow', description: 'Affects a single user workflow' },
  { value: 'multiple_workflows', label: 'Multiple Workflows', description: 'Affects multiple workflows' },
  { value: 'multiple_products', label: 'Multiple Products', description: 'Affects multiple products' },
  { value: 'company_wide', label: 'Company-Wide', description: 'Company-wide or strategic impact' },
];

// ============================================================================
// Completion Requirements for High-Altitude Items
// ============================================================================

/**
 * Completion requirements for Initiatives and Outcomes
 * 
 * At least one of these must be provided before an item can be
 * considered structurally complete.
 */
export interface AltitudeCompletionRequirements {
  /** Measurable outcome or leading indicator of success */
  successDefinition?: string;
  
  /** What exists after the first milestone */
  firstDeliverable?: string;
  
  /** Owner or accountable function */
  owner?: string;
}

// ============================================================================
// Constraint Types
// ============================================================================

/**
 * Types of constraint violations for leveling
 */
export type LevelingConstraintType = 
  | 'standalone_violation'      // High-altitude item without parent/container
  | 'completion_missing'        // Initiative/Outcome missing completion requirements
  | 'altitude_mismatch'         // Declared altitude inconsistent with scoping inputs
  | 'parent_gap_violation'      // Parent-child altitude gap not exactly 1 level
  | 'atomic_violation';         // Task has children

/**
 * A constraint violation for an item
 */
export interface LevelingConstraintViolation {
  itemId: string;
  itemTitle: string;
  constraintType: LevelingConstraintType;
  message: string;
  severity: 'error' | 'warning';
  suggestedAction?: string;
}

/**
 * Labels for constraint types
 */
export const CONSTRAINT_TYPE_LABELS: Record<LevelingConstraintType, string> = {
  standalone_violation: 'Needs Parent or Container',
  completion_missing: 'Missing Completion Requirements',
  altitude_mismatch: 'Altitude Mismatch',
  parent_gap_violation: 'Invalid Parent-Child Gap',
  atomic_violation: 'Task Cannot Have Children',
};

// ============================================================================
// Altitude Computation
// ============================================================================

/**
 * Result of computing altitude from scoping inputs
 * 
 * Altitude is the rounded average of the three dimension scores.
 * This is NOT a prediction - it IS the altitude.
 */
export interface ComputedAltitude {
  level: AltitudeLevel;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  /** Individual dimension scores used in computation */
  dimensionScores: {
    timeHorizon?: number;
    workSize?: number;
    impactSurface?: number;
  };
}

/**
 * @deprecated Use ComputedAltitude instead
 */
export interface AltitudePrediction {
  predictedAltitude: AltitudeLevel;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

/**
 * @deprecated Altitude is now computed, not declared vs predicted
 */
export interface AltitudeConsistencyCheck {
  isConsistent: boolean;
  declaredAltitude: AltitudeLevel;
  predictedAltitude: AltitudeLevel;
  warning?: string;
  suggestedAltitude: AltitudeLevel;
}

// ============================================================================
// Comparison Cell for Leveling View
// ============================================================================

/**
 * Represents a cell in the Altitude x CandidateType grid (5x6)
 */
export interface LevelingGridCell {
  altitudeLevel: AltitudeLevel;
  candidateType: string; // CandidateLanguageType from lifecycle.ts
  itemIds: string[];
  itemCount: number;
  hasViolations: boolean;
}

/**
 * Summary stats for the leveling dashboard
 */
export interface LevelingSummary {
  totalItems: number;
  byAltitudeLevel: Record<AltitudeLevel, number>;
  byCandidateType: Record<string, number>;
  byLifecycleState: Record<AltitudeLifecycleState, number>;
  violationCount: number;
  pendingReviewCount: number;
}
